import type { ProjectPLBucket } from '../types';
import type { DesignerInvoiceDoc } from './designerInvoiceService';

/** Approved designer-invoice cash credited to a job on Project P&L (job-direct only). */
export type DesignerInvoiceJobCash = {
  id: string;
  invoiceId: string;
  vendor: string;
  invoiceNumber: string;
  description: string;
  amount: number;
  dealId: string;
  txnDate: string;
  plBucket: ProjectPLBucket;
};

function parseIsoDate(value: unknown): string | null {
  if (typeof value !== 'string' || !value.trim()) return null;
  const iso = /^(\d{4}-\d{2}-\d{2})/.exec(value.trim());
  return iso ? iso[1] : null;
}

function designerLinePlBucket(bucket: string | undefined): ProjectPLBucket {
  if (bucket === 'training_onboarding') return 'overhead';
  if (bucket === 'external_engineering' || bucket === 'other') return 'engineering';
  return 'design';
}

export type DesignerInvoiceDedupeOptions = {
  /** qbExpenses doc ids already on the P&L (designerInvoices.qbExpenseId points here). */
  qbExpenseIds?: Set<string>;
  /** QuickBooks Bill Ids already mirrored in qbExpenses (designerInvoices.qboBillId). */
  qboBillIds?: Set<string>;
};

/**
 * True when the approved invoice already exists on the P&L as a synced
 * QuickBooks bill (qbExpenses). The bill is the QB truth, so the designer
 * invoice rows must not be added a second time.
 */
export function designerInvoiceCoveredByQbExpense(
  inv: Pick<DesignerInvoiceDoc, 'qbExpenseId' | 'qboBillId'>,
  opts?: DesignerInvoiceDedupeOptions,
): boolean {
  if (!opts) return false;
  const expenseId = (inv.qbExpenseId || '').trim();
  if (expenseId && opts.qbExpenseIds?.has(expenseId)) return true;
  const billId = (inv.qboBillId || '').trim();
  if (billId && opts.qboBillIds?.has(billId)) return true;
  return false;
}

/** Build the dedupe sets from the synced qbExpenses rows on the P&L. */
export function qbExpenseDedupeSets(
  expenses: Array<{ id: string; quickbooksId?: string | null; entityType?: string | null }>,
): Required<DesignerInvoiceDedupeOptions> {
  const qbExpenseIds = new Set<string>();
  const qboBillIds = new Set<string>();
  for (const e of expenses) {
    if (e.id) qbExpenseIds.add(e.id);
    const qbId = (e.quickbooksId || '').trim();
    if (qbId && (e.entityType || 'bill') === 'bill') qboBillIds.add(qbId);
  }
  return { qbExpenseIds, qboBillIds };
}

/** Flatten approved invoices into per-deal job-direct cash rows for Project P&L. */
export function flattenApprovedDesignerInvoiceJobCash(
  invoices: DesignerInvoiceDoc[],
  opts?: DesignerInvoiceDedupeOptions,
): DesignerInvoiceJobCash[] {
  const out: DesignerInvoiceJobCash[] = [];
  for (const inv of invoices) {
    if (inv.status !== 'approved') continue;
    // Already on the P&L as the synced QuickBooks bill — skip the whole invoice.
    if (designerInvoiceCoveredByQbExpense(inv, opts)) continue;
    const txnDate =
      parseIsoDate(inv.invoiceDate)
      || (inv.approvedAt ? inv.approvedAt.toISOString().slice(0, 10) : null)
      || new Date().toISOString().slice(0, 10);
    const lines = inv.allocations?.length ? inv.allocations : inv.proposedLines;
    const hasJobPo = (inv.purchaseRequisitionIds || []).length > 0;
    for (const line of lines) {
      if (line.phase === 'overhead') continue;
      const dealId = (line.dealId || '').trim();
      if (!dealId) continue;
      const amount = Number(line.amount) || 0;
      if (amount <= 0) continue;
      const plBucket = designerLinePlBucket(line.bucket);
      // Issued job POs consume design/engineering committed; skip those actuals to avoid double-count.
      if (hasJobPo && (plBucket === 'design' || plBucket === 'engineering')) continue;
      out.push({
        id: `designer-invoice:${inv.id}:${line.id}`,
        invoiceId: inv.id,
        vendor: inv.vendor || 'Designer',
        invoiceNumber: inv.invoiceNumber || inv.id,
        description: (line.description || inv.vendor || 'Designer invoice').trim(),
        amount,
        dealId,
        txnDate,
        plBucket,
      });
    }
  }
  return out;
}

export function designerInvoiceJobCashForDeal(
  rows: DesignerInvoiceJobCash[],
  dealId: string,
): DesignerInvoiceJobCash[] {
  return rows.filter(r => r.dealId === dealId);
}
